import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';
import moment from 'moment';

import { useFormatMessage, useFormatAddress } from 'hooks';
import Table from 'components/Table';
import { fetchEntities, entitiesCleanUp } from 'state/actions/stations';
import { fetchLastEntities as fetchLastStationsData } from 'state/actions/stationsData';
import {
  fetchEntities as fetchStationsUsersGroupsData,
  entitiesCleanUp as stationsUsersGroupsCleanUp,
} from 'state/actions/stationsUsersGroups';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import { search as searchInObject } from 'utils';
import {
  getLastStatus,
  getLastStatusValue,
  getLastStatusValueAsInt,
} from 'utils/hdrainHelper';
import CONSTANTS from 'utils/constants';
import classes from './Stations.module.scss';

const Stations = () => {
  const entitiesName = 'stations';
  // const entityName = "station";
  const i18nEntityName = 'Stations';
  const i18nScreenName = 'StationsList';

  // Actions
  // const fetchEntities = fetchStations;
  // const deleteEntity = deleteUser;
  // const entitiesCleanup = {stationsCleanUp};

  // Paths
  const PATH_ADD_ENTITY = paths.ADD_STATION;
  // const PATH_SEE_ENTITY = paths.SEE_STATION;

  const {
    entitiesList = [],
    isAuth,
    isAdmin,
    isInstaller,
    error,
    loading,
    deleted,
    stationsLastStatus = {},
    lastDataIsLoading,
    stationsUsersGroups = [],
  } = useSelector(
    (state) => ({
      entitiesList: state[entitiesName].data,
      isAuth: state.auth.userData.role > 0,
      isInstaller: CONSTANTS.IS_USER_ROLE_INSTALLER[state.auth.userData.role],
      isAdmin: CONSTANTS.IS_USER_ROLE_ADMIN[state.auth.userData.role],
      error: state[entitiesName].error,
      loading: state[entitiesName].loading,
      deleted: state[entitiesName].deleted,
      stationsLastStatus: state.stationsData.lastStatus,
      lastDataIsLoading: state.stationsData.lastDataIsLoading,
      stationsUsersGroups: state.stationsUsersGroups.data,
    }),
    shallowEqual
  );

  const [search, setSearch] = useState('');
  const [stationsWithGroups, setStationsWithGroups] = useState([]);
  const [searchedStations, setSearchedStations] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    entityId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuth && entitiesList.length === 0) {
      dispatch(fetchEntities());
    }

    return () => dispatch(entitiesCleanUp());
  }, [dispatch, isAuth, entitiesList]);

  useEffect(() => {
    if (isAuth && stationsUsersGroups.length === 0) {
      dispatch(fetchStationsUsersGroupsData());
    }

    return () => dispatch(stationsUsersGroupsCleanUp());
  }, [dispatch, isAuth, stationsUsersGroups]);

  useEffect(() => {
    // Add usersStationsGroups in entities
    const newStationsWithGroups = entitiesList.map((station) => {
      const { id } = station;
      const stationsUsersGroupsForStation = stationsUsersGroups.filter(
        (sug) => {
          const { stationsIds = [] } = sug;
          return stationsIds.includes(id);
        }
      );

      const stationWithGroups = {
        ...station,
        stationsUsersGroups: stationsUsersGroupsForStation,
      };
      return stationWithGroups;
    });
    setStationsWithGroups(newStationsWithGroups);
  }, [entitiesList, stationsUsersGroups]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        entityId: null,
        isOpen: !prevState.isOpen,
      }));
    }
  }, [deleted, loading]);

  useEffect(() => {
    if (!lastDataIsLoading) {
      const serialNumbersAsString = entitiesList
        .map((entity) => {
          const { serialNumber } = entity;
          if (typeof serialNumber === 'number') {
            return String(serialNumber);
          }
          return serialNumber;
        })
        .filter((serialNumber) => {
          return serialNumber && serialNumber.length > 0;
        });
      dispatch(fetchLastStationsData(serialNumbersAsString));
    }
  }, [dispatch, entitiesList]);

  const onCloseModalHandler = () => {
    setDeleteModal({ entityId: null, isOpen: false });
  };

  useEffect(() => {
    const data = search
      ? stationsWithGroups.filter((el) => {
          const clonedElem = {
            address: {
              city: el.address.city,
              country: el.address.country,
            },
            name: el.name,
            serialNumber: el.serialNumber,
          };

          // Delete these fields from search
          delete clonedElem.id;

          // In group, keep only name field
          clonedElem.stationsUsersGroups = el.stationsUsersGroups.map((sug) => {
            return { name: sug.name };
          });

          return searchInObject(clonedElem, search);
        })
      : stationsWithGroups;

    setSearchedStations(data);
  }, [search, stationsWithGroups]);

  const commonsColumns = [
    {
      id: 'healthDataSignal',
      Header: () => {
        return (
          <span className="icon">
            <i className="mdi mdi-hospital-box-outline" />
          </span>
        );
      },
      accessor: (row) => {
        const { id } = row;
        let value = -1;
        if (stationsLastStatus[id] && stationsLastStatus[id].signal) {
          value = stationsLastStatus[id].signal.health;
        }
        // If <1, sorting does not work
        return value * 100;
      },
      Cell: ({ row }) => {
        const { id } = row.original;
        let value = -1;
        let content = '';
        let className = 'has-text-danger-dark';
        if (stationsLastStatus[id] && stationsLastStatus[id].signal) {
          value = stationsLastStatus[id].signal.health;
          content = `${parseInt(value * 100, 10)}%`;

          if (value >= 0.8) {
            className = 'has-text-success';
          } else if (value >= 0.5) {
            className = 'has-text-warning-dark';
          }
        }
        return <span className={className}>{content}</span>;
      },
    },

    {
      Header: useFormatMessage(`${i18nEntityName}.name`),
      accessor: 'name',
    },
    {
      Header: '#',
      accessor: 'serialNumber',
    },
    {
      id: 'incident',
      Header: () => {
        return (
          <span className="icon">
            <i className="mdi mdi- mdi-alert-circle-outline" />
          </span>
        );
      },
      accessor: (row) => {
        const { id } = row;
        const signalValue = getLastStatusValueAsInt(
          stationsLastStatus[id],
          'signal'
        );
        const statusValue = getLastStatusValueAsInt(
          stationsLastStatus[id],
          'status'
        );

        const localesValue = getLastStatusValueAsInt(
          stationsLastStatus[id],
          'locales'
        );
        return signalValue + statusValue + localesValue;
      },
      Cell: ({ row }) => {
        const { values = {} } = row;
        const { incident = -10 } = values;

        const iconName = 'alert-circle-outline';
        let className = 'has-text-danger-dark';

        if (incident >= 5) {
          className = 'has-text-warning-dark';
        }

        return (
          incident < 30 && (
            <div className={`has-text-centered is-loading ${className}`}>
              {lastDataIsLoading || (
                <span className="icon">
                  <i className={`mdi mdi- mdi-${iconName}`} />
                </span>
              )}
            </div>
          )
        );
      },
    },

    {
      id: 'dataSignal',
      Header: useFormatMessage(`${i18nScreenName}.dataSignal`),
      accessor: (row) => {
        const { id } = row;
        const value = getLastStatusValueAsInt(stationsLastStatus[id], 'signal');
        return value;
      },
      Cell: ({ row }) => {
        const { id } = row.original;

        const lastSignalStatus = getLastStatus(
          stationsLastStatus[id],
          'signal'
        );

        const lastSignalStatusValue =
          lastSignalStatus && lastSignalStatus.status;
        const lastSignalEpoch = lastSignalStatus && lastSignalStatus.epoch;
        let lastSignalDateStr = 'N/A';
        if (lastSignalEpoch) {
          lastSignalDateStr = moment(lastSignalEpoch).utc().toISOString();
        }
        let iconName = 'signal-cellular-outline';
        let className = 'has-text-danger-dark';

        switch (lastSignalStatusValue) {
          case CONSTANTS.SIGNALS_STATUS.OK:
            iconName = 'signal-cellular-3';
            className = 'has-text-success';
            break;
          case CONSTANTS.SIGNALS_STATUS.LOW:
            iconName = 'signal-cellular-1';
            className = 'has-text-warning-dark';
            break;
          default:
            iconName = 'signal-cellular-outline';
            className = 'has-text-danger-dark';
            break;
        }
        return (
          <div
            className={`has-text-centered is-loading ${className}`}
            title={lastSignalDateStr}
          >
            <span className="icon">
              {lastDataIsLoading || <i className={`mdi mdi-${iconName}`} />}
            </span>
          </div>
        );
      },
    },

    {
      Header: useFormatMessage(`${i18nScreenName}.batteryLevel`),
      id: 'batteryLevel',
      accessor: (row) => {
        const { id } = row;
        const value = getLastStatusValueAsInt(stationsLastStatus[id], 'status');
        return value;
      },
      Cell: ({ row }) => {
        const { id } = row.original;
        const lastBatteryStatus = getLastStatusValue(
          stationsLastStatus[id],
          'status'
        );
        let iconName = 'battery-50';
        let className = 'has-text-warning-dark';

        switch (lastBatteryStatus) {
          case CONSTANTS.BATTERY_STATUS.OK:
            iconName = 'battery';
            className = 'has-text-success';
            break;
          default:
            iconName = 'battery-50';
            className = 'has-text-warning-dark';
            break;
        }
        return (
          <div className={`has-text-centered ${className}`}>
            <span className="icon">
              {lastDataIsLoading || <i className={`mdi mdi-${iconName}`} />}
            </span>
          </div>
        );
      },
    },
    {
      Header: 'TLNB',
      id: 'TLNB',
      accessor: (row) => {
        const { id } = row;
        const value = getLastStatusValueAsInt(
          stationsLastStatus[id],
          'locales'
        );

        return value;
      },
      Cell: ({ row }) => {
        const { id } = row.original;

        const lastLocales = getLastStatus(stationsLastStatus[id], 'locales');
        const lastLocalesValue = lastLocales && lastLocales.value;
        const lastLocalesValueStr =
          (lastLocalesValue && `${lastLocalesValue}°C`) || '';
        const lastLocalesStatus = lastLocales && lastLocales.status;
        const lastLocalesEpoch = lastLocales && lastLocales.epoch;
        let lastLocalesDateStr = 'N/A';
        if (lastLocalesEpoch) {
          lastLocalesDateStr = moment(lastLocalesEpoch).utc().toISOString();
        }

        const iconName = 'coolant-temperature';
        let className = 'has-text-danger-dark';

        switch (lastLocalesStatus) {
          case CONSTANTS.BASE_STATUS.OK:
            className = 'has-text-success';
            break;
          case CONSTANTS.BASE_STATUS.LOW:
            className = 'has-text-warning-dark';
            break;
          default:
            className = 'has-text-danger-dark';
            break;
        }
        return (
          <div
            className={`has-text-centered ${className}`}
            title={`${lastLocalesValueStr} - ${lastLocalesDateStr}`}
          >
            <span className="icon">
              {lastDataIsLoading || <i className={`mdi mdi-${iconName}`} />}
            </span>
          </div>
        );
      },
    },
  ];
  const installerColumns = [
    ...commonsColumns,
    {
      Header: useFormatMessage(`${i18nEntityName}.address`),
      accessor: 'address',
      Cell: ({ row }) => useFormatAddress(row.original.address),
    },
    {
      id: 'createdAt',
      Header: () => {
        return (
          <span className="icon">
            <i className="mdi mdi-calendar" />
          </span>
        );
      },
      accessor: (row) => {
        const { createdAt = 0 } = row;
        const ts = new Date(createdAt).getTime();
        return ts;
      },
      Cell: ({ row }) => {
        const { createdAt = 0 } = row && row.original;

        const createdDate = moment(createdAt).utc();
        const dateIso = createdDate.toISOString();
        const dateStr = createdDate.format('DD/MM/YY');
        return <span title={dateIso}>{dateStr}</span>;
      },
    },
  ];

  const adminColumns = [
    ...installerColumns,
    {
      Header: useFormatMessage(`${i18nEntityName}.groups`),
      accessor: (row) => {
        const _stationsUsersGroups = (row && row.stationsUsersGroups) || [];

        return _stationsUsersGroups.length;
      },
      Cell: ({ row }) => {
        const _stationsUsersGroups =
          (row && row.original && row.original.stationsUsersGroups) || [];

        const groupNames = _stationsUsersGroups.map((group) => {
          return group.name;
        });
        const cell = groupNames.join(', ');
        return cell;
      },
    },
  ];
  const actionColumn = {
    Header: '',
    id: 'actions',
    accessor: 'actions',
    Cell: ({ row }) => (
      <div className="buttons is-right">
        <Link
          to={`/${entitiesName}/${row.original.id}/profile`}
          className="button is-small is-primary"
        >
          <span className="icon is-small">
            <i className="mdi mdi-eye" />
          </span>
        </Link>

        {/* <button
            type="button"
            className="button is-small is-danger"
            onClick={() => onRemoveButtonClickHandler(row.original.id)}
          >
            <span className="icon is-small">
              <i className="mdi mdi-trash-can" />
            </span>
          </button> */}
      </div>
    ),
    disableSortBy: true,
  };

  // const deleteMessage = useFormatMessage(i18nEntityName +'.delete');

  // const confirmMessage = useFormatMessage(i18nEntityName +'.confirm');

  // const permDeleteMessage = useFormatMessage(i18nEntityName +'.permDelete');

  // const cancelMessage = useFormatMessage(i18nEntityName +'.cancel');
  let columns = commonsColumns;
  if (isInstaller) {
    columns = installerColumns;
  } else if (isAdmin) {
    columns = adminColumns;
  }
  columns.push(actionColumn);

  const redirect = !isAuth && <Redirect to={paths.ROOT} />;
  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          // confirmButtonMessage={deleteMessage}
          // title={confirmMessage}
          // body={permDeleteMessage}
          // cancelButtonMessage={cancelMessage}
          // onConfirmation={onDeleteEntityHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">
                  {useFormatMessage(`${i18nEntityName}.entities`)} (
                  {entitiesList.length}) {lastDataIsLoading && <ClipLoader />}
                </h1>
              </div>
            </div>
            {isAdmin && (
              <div className="level-right">
                <div className="level-item">
                  <Link to={PATH_ADD_ENTITY} className="button">
                    {useFormatMessage(`${i18nEntityName}.newEntity`)}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage(`${i18nEntityName}.search`)}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading ? (
              <ClipLoader />
            ) : (
              <Table
                columns={columns}
                data={searchedStations}
                options={{
                  hasRowClick: true,
                  getRowLinkPath: (row) => {
                    return `/stations/${row.original.id}/profile`;
                  },
                }}
              />
            )}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Stations;
